import React from 'react';
import { Button } from '@/components/ui/button';
import { Mic, MicOff, Send, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AudioRecorderProps {
  isRecording: boolean;
  duration: number;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onCancelRecording: () => void;
  formatDuration: (seconds: number) => string;
  disabled?: boolean;
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({
  isRecording,
  duration,
  onStartRecording,
  onStopRecording,
  onCancelRecording,
  formatDuration,
  disabled = false
}) => {
  if (!isRecording) {
    return (
      <Button
        onClick={onStartRecording}
        disabled={disabled}
        className="h-12 w-12 p-0 rounded-full glass border border-border/30 hover:bg-primary/10 transition-smooth shrink-0"
      >
        <Mic className="w-5 h-5 text-primary" />
      </Button>
    );
  }

  return (
    <div className="flex items-center justify-between glass border border-border/30 rounded-full px-2 sm:px-3 py-2 w-full max-w-full overflow-hidden min-w-0">
      {/* Recording indicator */}
      <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0 min-w-0">
        <div className="w-2 h-2 sm:w-3 sm:h-3 bg-destructive rounded-full animate-pulse flex-shrink-0"></div>
        <span className="text-xs sm:text-sm font-medium text-foreground truncate">
          {formatDuration(duration)}
        </span>
      </div>
      
      {/* Waveform animation */}
      <div className="flex items-center space-x-0.5 sm:space-x-1 flex-1 justify-center min-w-0 mx-1 sm:mx-2">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className={cn(
              "w-0.5 sm:w-1 bg-primary rounded-full transition-all duration-300 flex-shrink-0",
              "animate-pulse"
            )}
            style={{
              height: Math.random() * 15 + 8 + 'px',
              animationDelay: i * 0.1 + 's'
            }}
          />
        ))}
      </div>

      {/* Controls */}
      <div className="flex items-center space-x-0.5 sm:space-x-1 flex-shrink-0">
        <Button
          onClick={onCancelRecording}
          variant="ghost"
          size="sm"
          className="h-6 w-6 sm:h-8 sm:w-8 p-0 rounded-full hover:bg-destructive/10 flex-shrink-0"
        >
          <X className="w-3 h-3 sm:w-4 sm:h-4 text-destructive" />
        </Button>
        
        <Button
          onClick={onStopRecording}
          className="h-6 w-6 sm:h-8 sm:w-8 p-0 rounded-full gradient-primary flex-shrink-0"
        >
          <Send className="w-3 h-3 sm:w-4 sm:h-4" />
        </Button>
      </div>
    </div>
  );
};

export default AudioRecorder;